// src/pages/AdminDashboard.js – Admin panel (role === 'admin' only)
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { adminAPI, missionsAPI } from '../utils/api';

const RULE_TYPES = ['contains_tag','contains_attr','contains_text','css_property','js_output','regex'];
const EMPTY = {
  title:'', description:'', instructions:'', hint:'', track:'html', order:1,
  difficulty:'beginner', starterCode:'', xpReward:50, coinsReward:20,
  validationRules:[{ type:'contains_text', value:'', message:'' }],
  badgeReward:{ id:'', name:'', icon:'' },
};

const iBase = {
  width:'100%', background:'rgba(0,245,255,0.04)', border:'1px solid rgba(0,245,255,0.15)',
  borderRadius:'5px', padding:'8px 11px', color:'#e2e8f0',
  fontFamily:'Rajdhani,sans-serif', fontSize:'14px', outline:'none', resize:'vertical',
};
const lbl = { display:'block', fontSize:'10px', fontWeight:700, color:'rgba(255,255,255,0.32)',
               letterSpacing:'1.5px', marginBottom:'4px', fontFamily:'Orbitron,monospace' };

export default function AdminDashboard() {
  const { user, showToast } = useAuth();
  const navigate = useNavigate();
  const [tab,       setTab]       = useState('overview');
  const [stats,     setStats]     = useState(null);
  const [users,     setUsers]     = useState([]);
  const [missions,  setMissions]  = useState([]);
  const [form,      setForm]      = useState(EMPTY);
  const [editId,    setEditId]    = useState(null);
  const [saving,    setSaving]    = useState(false);
  const [mFilter,   setMFilter]   = useState('all');

  useEffect(() => { if (user && user.role !== 'admin') navigate('/'); }, [user, navigate]);

  useEffect(() => {
    if (tab === 'overview') adminAPI.stats().then(r=>setStats(r.data)).catch(()=>setStats({
      stats:{totalUsers:0,activeMissions:0,totalMissions:0}, topUsers:[], recentUsers:[]
    }));
    if (tab === 'users')    adminAPI.users().then(r=>setUsers(r.data.users)).catch(()=>setUsers([]));
    if (tab === 'missions') missionsAPI.getAll().then(r=>setMissions(r.data.missions)).catch(()=>setMissions([]));
  }, [tab]);

  const save = async () => {
    if (!form.title || !form.track) { showToast('Error','Title and track required','error'); return; }
    setSaving(true);
    try {
      if (editId) { await missionsAPI.update(editId, form); showToast('Updated!', form.title); }
      else        { await missionsAPI.create(form);         showToast('Created!', form.title,'success'); }
      setForm(EMPTY); setEditId(null); setTab('missions');
      missionsAPI.getAll().then(r=>setMissions(r.data.missions)).catch(()=>{});
    } catch (e) { showToast('Error', e.response?.data?.message || 'Save failed','error'); }
    finally { setSaving(false); }
  };

  const editMission = m => { setForm({ ...EMPTY, ...m, badgeReward:m.badgeReward||{id:'',name:'',icon:''} }); setEditId(m._id); setTab('create'); };
  const deactivate  = async (id, title) => {
    if (!window.confirm(`Deactivate "${title}"`)) return;
    await adminAPI.deactivateMission(id).catch(()=>{});
    showToast('Deactivated', title);
    setMissions(m => m.filter(x => x._id !== id));
  };

  const setF = (k,v) => setForm(f=>({...f,[k]:v}));
  const addRule = () => setForm(f=>({...f, validationRules:[...f.validationRules,{type:'contains_text',value:'',message:''}]}));
  const removeRule = i => setForm(f=>({...f, validationRules:f.validationRules.filter((_,idx)=>idx!==i)}));
  const updateRule = (i,k,v) => setForm(f=>({...f, validationRules:f.validationRules.map((r,idx)=>idx===i?{...r,[k]:v}:r)}));

  const filtered = mFilter === 'all' ? missions : missions.filter(m=>m.track===mFilter);

  if (!user || user.role !== 'admin') return null;

  const TABS = [
    { id:'overview', label:'📊 OVERVIEW' },
    { id:'users',    label:'👥 USERS'    },
    { id:'missions', label:'🎯 MISSIONS' },
    { id:'create',   label:'+ CREATE'    },
  ];

  return (
    <div style={{ height:'100%', overflowY:'auto', padding:'26px', background:'#0a0a0f' }}>
      <div style={{ display:'flex', alignItems:'center', gap:'12px', marginBottom:'22px' }}>
        <div style={{ fontFamily:'var(--font-game)', fontSize:'16px', fontWeight:700, color:'#00f5ff', letterSpacing:'3px' }}>ADMIN PANEL</div>
        <span style={{ background:'rgba(255,68,68,0.1)', border:'1px solid rgba(255,68,68,0.3)', borderRadius:'4px', padding:'2px 9px', fontSize:'10px', color:'#ff6464', fontFamily:'var(--font-game)', letterSpacing:'1px' }}>RESTRICTED</span>
      </div>

      {/* Tabs */}
      <div style={{ display:'flex', gap:'5px', marginBottom:'22px', borderBottom:'1px solid rgba(0,245,255,0.09)', paddingBottom:'11px' }}>
        {TABS.map(t => (
          <button key={t.id} onClick={() => { setTab(t.id); if(t.id==='create'){setForm(EMPTY);setEditId(null);} }} style={{
            padding:'7px 16px', fontFamily:'var(--font-game)', fontSize:'10px', fontWeight:700, letterSpacing:'1px',
            border:`1px solid ${tab===t.id?'rgba(0,245,255,0.4)':'rgba(255,255,255,0.07)'}`,
            borderRadius:'5px', cursor:'pointer',
            background: tab===t.id?'rgba(0,245,255,0.1)':'rgba(255,255,255,0.02)',
            color: tab===t.id?'#00f5ff':'rgba(255,255,255,0.38)',
          }}>{t.label}</button>
        ))}
      </div>

      {/* ── OVERVIEW ── */}
      {tab === 'overview' && stats && (
        <div style={{ display:'flex', flexDirection:'column', gap:'22px' }}>
          <div style={{ display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:'12px' }}>
            {[{v:stats.stats?.totalUsers,l:'TOTAL USERS',c:'#00f5ff'},
              {v:stats.stats?.activeMissions,l:'ACTIVE MISSIONS',c:'#39ff14'},
              {v:stats.stats?.totalMissions,l:'TOTAL MISSIONS',c:'#ffd700'}].map(s=>(
              <div key={s.l} className="stat-card">
                <div style={{ fontFamily:'var(--font-game)', fontSize:'26px', fontWeight:700, color:s.c }}>{s.v||0}</div>
                <div className="stat-lbl">{s.l}</div>
              </div>
            ))}
          </div>
          {(stats.topUsers||[]).length > 0 && (
            <div>
              <div className="section-title">TOP PLAYERS</div>
              <div style={{ background:'rgba(13,17,23,0.8)', border:'1px solid rgba(0,245,255,0.09)', borderRadius:'10px', overflow:'hidden' }}>
                {stats.topUsers.map((u,i)=>(
                  <div key={u.username} style={{ display:'flex', alignItems:'center', gap:'12px', padding:'12px 18px', borderBottom:'1px solid rgba(0,245,255,0.04)' }}>
                    <span style={{ fontFamily:'var(--font-game)', fontSize:'13px', fontWeight:700, color:i===0?'#ffd700':i===1?'#c0c0c0':'#cd7f32', width:'22px' }}>{i+1}</span>
                    <span style={{ fontWeight:700, flex:1 }}>{u.username}</span>
                    <span style={{ fontSize:'12px', color:'#7c3aed' }}>⚡{u.xp}</span>
                    <span style={{ fontSize:'12px', color:'rgba(255,255,255,0.3)' }}>LVL {u.level}</span>
                    <span style={{ fontSize:'12px', color:'#39ff14' }}>✓{u.completedMissions?.length||0}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* ── USERS ── */}
      {tab === 'users' && (
        <div>
          <div style={{ fontSize:'12px', color:'rgba(255,255,255,0.3)', marginBottom:'14px' }}>{users.length} users</div>
          <div style={{ background:'rgba(13,17,23,0.8)', border:'1px solid rgba(0,245,255,0.09)', borderRadius:'10px', overflow:'hidden' }}>
            <div style={{ display:'grid', gridTemplateColumns:'1.2fr 1.5fr 60px 60px 70px 80px 90px', gap:'8px', padding:'9px 18px',
                          background:'rgba(0,245,255,0.04)', borderBottom:'1px solid rgba(0,245,255,0.07)',
                          fontFamily:'var(--font-game)', fontSize:'9px', color:'rgba(255,255,255,0.25)', letterSpacing:'1.5px' }}>
              <span>USER</span><span>EMAIL</span><span>LVL</span><span>XP</span><span>COINS</span><span>DONE</span><span>ROLE</span>
            </div>
            {users.length === 0 ? (
              <div style={{ padding:'32px', textAlign:'center', color:'rgba(255,255,255,0.2)', fontSize:'13px' }}>Connect backend to see users</div>
            ) : users.map(u=>(
              <div key={u._id} style={{ display:'grid', gridTemplateColumns:'1.2fr 1.5fr 60px 60px 70px 80px 90px', gap:'8px', padding:'11px 18px',
                                        borderBottom:'1px solid rgba(0,245,255,0.03)', alignItems:'center', fontSize:'12px' }}>
                <span style={{ fontWeight:700 }}>{u.username}</span>
                <span style={{ color:'rgba(255,255,255,0.35)', fontSize:'11px' }}>{u.email}</span>
                <span style={{ color:'#00f5ff' }}>{u.level}</span>
                <span style={{ color:'#7c3aed' }}>{u.xp}</span>
                <span style={{ color:'#ffd700' }}>{u.coins}</span>
                <span style={{ color:'#39ff14' }}>{u.completedMissions?.length||0}</span>
                {u._id === user?._id ? (
                  <span style={{ fontSize:'11px', color:'rgba(0,245,255,0.5)', alignSelf:'center' }}>YOU</span>
                ) : (
                  <select value={u.role||'user'} onChange={async e=>{
                    await adminAPI.setRole(u._id,e.target.value).catch(()=>{});
                    showToast('Role updated',`${u.username} → ${e.target.value}`);
                    setUsers(us=>us.map(x=>x._id===u._id?{...x,role:e.target.value}:x));
                  }} style={{...iBase,padding:'3px 6px',fontSize:'11px',width:'auto'}}>
                    <option value="user">user</option>
                    <option value="admin">admin</option>
                  </select>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── MISSIONS ── */}
      {tab === 'missions' && (
        <div>
          <div style={{ display:'flex', gap:'6px', marginBottom:'14px', flexWrap:'wrap' }}>
            {['all','html','css','javascript'].map(t=>(
              <button key={t} onClick={()=>setMFilter(t)} style={{
                padding:'5px 13px', fontFamily:'var(--font-game)', fontSize:'9px', fontWeight:700, letterSpacing:'1px',
                border:`1px solid ${mFilter===t?'rgba(0,245,255,0.4)':'rgba(255,255,255,0.08)'}`,
                borderRadius:'4px', cursor:'pointer',
                background: mFilter===t?'rgba(0,245,255,0.1)':'rgba(255,255,255,0.02)',
                color: mFilter===t?'#00f5ff':'rgba(255,255,255,0.38)',
              }}>{t.toUpperCase()}</button>
            ))}
            <span style={{ marginLeft:'auto', fontSize:'11px', color:'rgba(255,255,255,0.25)', alignSelf:'center' }}>{filtered.length} missions</span>
          </div>
          <div style={{ display:'flex', flexDirection:'column', gap:'7px' }}>
            {filtered.length === 0 && <div style={{ padding:'32px', textAlign:'center', color:'rgba(255,255,255,0.2)', fontSize:'13px' }}>No missions. Connect backend or create one.</div>}
            {filtered.map(m=>(
              <div key={m._id} style={{ display:'flex', alignItems:'center', gap:'10px', padding:'12px 16px',
                                        background:'rgba(13,17,23,0.8)', border:'1px solid rgba(0,245,255,0.07)',
                                        borderRadius:'7px' }}>
                <span style={{ fontFamily:'var(--font-game)', fontSize:'9px', color:'rgba(255,255,255,0.22)', width:'65px', letterSpacing:'1px' }}>{m.track?.toUpperCase()}</span>
                <span style={{ fontFamily:'var(--font-game)', fontSize:'10px', color:'rgba(255,255,255,0.2)', width:'20px' }}>#{m.order}</span>
                <span style={{ fontWeight:700, flex:1, fontSize:'13px' }}>{m.title}</span>
                <span style={{ fontSize:'11px', color: m.difficulty==='beginner'?'#39ff14':m.difficulty==='intermediate'?'#ffd700':'#ff6432' }}>{m.difficulty}</span>
                <span style={{ fontSize:'11px', color:'#7c3aed' }}>⚡{m.xpReward}</span>
                <span style={{ fontSize:'11px', color:'#ffd700' }}>🪙{m.coinsReward}</span>
                <button onClick={()=>editMission(m)} style={{ padding:'4px 10px', background:'rgba(0,245,255,0.07)', border:'1px solid rgba(0,245,255,0.18)', borderRadius:'4px', color:'#00f5ff', cursor:'pointer', fontSize:'10px', fontWeight:700, fontFamily:'var(--font-game)' }}>EDIT</button>
                <button onClick={()=>deactivate(m._id,m.title)} style={{ padding:'4px 10px', background:'rgba(255,68,68,0.06)', border:'1px solid rgba(255,68,68,0.18)', borderRadius:'4px', color:'#ff6464', cursor:'pointer', fontSize:'10px', fontWeight:700, fontFamily:'var(--font-game)' }}>DEL</button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── CREATE/EDIT ── */}
      {tab === 'create' && (
        <div style={{ maxWidth:'700px', display:'flex', flexDirection:'column', gap:'13px' }}>
          <div style={{ fontFamily:'var(--font-game)', fontSize:'11px', color:editId?'#ffd700':'#39ff14', letterSpacing:'2px' }}>
            {editId ? '✏️ EDITING MISSION' : '+ NEW MISSION'}
          </div>
          <div style={{ display:'grid', gridTemplateColumns:'2fr 1fr', gap:'11px' }}>
            <div><label style={lbl}>TITLE *</label><input style={iBase} value={form.title} onChange={e=>setF('title',e.target.value)} placeholder="Mission title" /></div>
            <div><label style={lbl}>TRACK *</label>
              <select style={iBase} value={form.track} onChange={e=>setF('track',e.target.value)}>
                <option value="html">HTML</option><option value="css">CSS</option><option value="javascript">JavaScript</option>
              </select></div>
          </div>
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr 1fr', gap:'11px' }}>
            <div><label style={lbl}>ORDER</label><input style={iBase} type="number" min="1" max="20" value={form.order} onChange={e=>setF('order',+e.target.value)} /></div>
            <div><label style={lbl}>DIFFICULTY</label>
              <select style={iBase} value={form.difficulty} onChange={e=>setF('difficulty',e.target.value)}>
                <option value="beginner">Beginner</option><option value="intermediate">Intermediate</option><option value="advanced">Advanced</option>
              </select></div>
            <div><label style={lbl}>XP REWARD</label><input style={iBase} type="number" value={form.xpReward} onChange={e=>setF('xpReward',+e.target.value)} /></div>
            <div><label style={lbl}>COINS</label><input style={iBase} type="number" value={form.coinsReward} onChange={e=>setF('coinsReward',+e.target.value)} /></div>
          </div>
          <div><label style={lbl}>DESCRIPTION</label><input style={iBase} value={form.description} onChange={e=>setF('description',e.target.value)} placeholder="One-line summary" /></div>
          <div><label style={lbl}>INSTRUCTIONS (HTML)</label><textarea style={{...iBase,height:'80px',fontFamily:'monospace',fontSize:'12px'}} value={form.instructions} onChange={e=>setF('instructions',e.target.value)} /></div>
          <div><label style={lbl}>HINT</label><input style={iBase} value={form.hint} onChange={e=>setF('hint',e.target.value)} placeholder="Helpful nudge" /></div>
          <div><label style={lbl}>STARTER CODE</label><textarea style={{...iBase,height:'70px',fontFamily:'monospace',fontSize:'12px'}} value={form.starterCode} onChange={e=>setF('starterCode',e.target.value)} /></div>

          <div>
            <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:'8px' }}>
              <label style={{...lbl,marginBottom:0}}>VALIDATION RULES ({form.validationRules.length})</label>
              <button onClick={addRule} style={{ padding:'4px 11px', background:'rgba(0,245,255,0.07)', border:'1px solid rgba(0,245,255,0.18)', borderRadius:'4px', color:'#00f5ff', cursor:'pointer', fontSize:'10px', fontWeight:700, fontFamily:'var(--font-game)' }}>+ ADD</button>
            </div>
            {form.validationRules.map((r,i)=>(
              <div key={i} style={{ display:'grid', gridTemplateColumns:'155px 1fr 1fr 34px', gap:'7px', marginBottom:'7px', alignItems:'center' }}>
                <select style={{...iBase,padding:'7px 9px',fontSize:'11px'}} value={r.type} onChange={e=>updateRule(i,'type',e.target.value)}>
                  {RULE_TYPES.map(t=><option key={t} value={t}>{t}</option>)}
                </select>
                <input style={{...iBase,fontSize:'12px',fontFamily:'monospace'}} placeholder="value" value={r.value} onChange={e=>updateRule(i,'value',e.target.value)} />
                <input style={{...iBase,fontSize:'12px'}} placeholder="error message for user" value={r.message} onChange={e=>updateRule(i,'message',e.target.value)} />
                <button onClick={()=>removeRule(i)} style={{ height:'34px', background:'rgba(255,68,68,0.07)', border:'1px solid rgba(255,68,68,0.18)', borderRadius:'4px', color:'#ff6464', cursor:'pointer', fontWeight:700 }}>✕</button>
              </div>
            ))}
            <div style={{ marginTop:'7px', padding:'8px 12px', background:'rgba(0,245,255,0.03)', border:'1px solid rgba(0,245,255,0.07)', borderRadius:'5px', fontSize:'10px', color:'rgba(255,255,255,0.25)', lineHeight:1.8, fontFamily:'monospace' }}>
              contains_tag → &lt;tag&gt; · contains_attr → attr= · contains_text → substring<br/>
              css_property → prop: · js_output → exact string · regex → pattern
            </div>
          </div>

          <div>
            <label style={lbl}>BADGE REWARD (optional)</label>
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 70px', gap:'9px' }}>
              <input style={iBase} placeholder="badge-id" value={form.badgeReward?.id||''} onChange={e=>setForm(f=>({...f,badgeReward:{...f.badgeReward,id:e.target.value}}))} />
              <input style={iBase} placeholder="Badge Name" value={form.badgeReward?.name||''} onChange={e=>setForm(f=>({...f,badgeReward:{...f.badgeReward,name:e.target.value}}))} />
              <input style={iBase} placeholder="🏆" value={form.badgeReward?.icon||''} onChange={e=>setForm(f=>({...f,badgeReward:{...f.badgeReward,icon:e.target.value}}))} />
            </div>
          </div>

          <div style={{ display:'flex', gap:'9px', paddingTop:'6px' }}>
            <button onClick={save} disabled={saving} style={{
              flex:1, padding:'13px', cursor:saving?'wait':'pointer',
              background: saving?'rgba(0,245,255,0.03)':'linear-gradient(135deg,rgba(0,245,255,0.14),rgba(124,58,237,0.14))',
              border:'1px solid rgba(0,245,255,0.32)', borderRadius:'7px',
              color:'#00f5ff', fontFamily:'var(--font-game)', fontSize:'12px', fontWeight:700, letterSpacing:'2px',
            }}>{saving ? '...' : editId ? 'UPDATE MISSION' : 'CREATE MISSION'}</button>
            {editId && <button onClick={()=>{setForm(EMPTY);setEditId(null);}} style={{ padding:'13px 18px', background:'rgba(255,255,255,0.03)', border:'1px solid rgba(255,255,255,0.08)', borderRadius:'7px', color:'rgba(255,255,255,0.45)', cursor:'pointer', fontFamily:'var(--font-game)', fontSize:'11px', fontWeight:700 }}>CANCEL</button>}
          </div>
        </div>
      )}
    </div>
  );
}
